import { ChatMessage, Participant } from "interface/communications"
import { Patient } from "interface/patientType"
import { User } from "interface/user"
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from "react"

export interface ChatContextType {
	patient: Patient
	provider?: User
	sender: User
	activeParticipant: Participant
	parentMessage?: ChatMessage | null
	threadId?: string | null
	noPriorComms?: boolean
	replyTo: (parentMessage: ChatContextType["parentMessage"]) => void
	toggleThread: (threadId: ChatContextType["threadId"]) => void
	setNoPriorComms: (noPriorComms: boolean) => boolean
}

const ChatContext = createContext<ChatContextType>({} as ChatContextType)

export const useChat = () => useContext(ChatContext)

export function ChatProvider({
	defaultValue,
	children,
}: {
	defaultValue?: Partial<ChatContextType>
	children: ReactNode | ((chatContext: ChatContextType) => ReactNode)
}) {
	const [state, dispatch] = useReducer(
		(state: Partial<ChatContextType>, action: Partial<ChatContextType>) => {
			return { ...state, ...action }
		},
		{
			activeParticipant: defaultValue?.activeParticipant,
			noPriorComms: true,
		},
	)
	const replyTo = useCallback(
		(parentMessage: ChatContextType["parentMessage"]) =>
			dispatch({ parentMessage }),
		[dispatch],
	)
	const toggleThread = useCallback(
		(threadId: ChatContextType["threadId"]) => dispatch({ threadId }),
		[dispatch],
	)
	const setNoPriorComms = useCallback(
		(noPriorComms: boolean) => dispatch({ noPriorComms }),
		[dispatch],
	)

	useEffect(() => {
		if (defaultValue?.activeParticipant?.id !== state.activeParticipant?.id) {
			dispatch({
				activeParticipant: defaultValue?.activeParticipant,
				parentMessage: null,
				threadId: null,
			})
		}
	}, [defaultValue?.activeParticipant, state.activeParticipant])

	const value = useMemo(
		() =>
			({
				...defaultValue,
				...state,
				replyTo,
				toggleThread,
				setNoPriorComms,
			}) as ChatContextType,
		[defaultValue, state, replyTo, toggleThread, setNoPriorComms],
	)

	return (
		<ChatContext value={value}>
			{typeof children === "function" ? children(value) : children}
		</ChatContext>
	)
}
